import { matchPath } from 'react-router-dom';

/**
 * A Dashboard füleinek EGYETLEN forrása. [Web UX A5, 2026-09-12]
 *
 * Eddig a fülek sorrendje a JSX-ben, a tartalom pedig kézzel számolt indexekkel
 * (`activeTab === (isAdmin ? 2 : 3)`) öt helyen élt: egy új feltételes fül beszúrása csendben
 * eltolta a mögötte lévőket, és rossz komponenst mutatott. Most a fül-lista szerepkörönként
 * itt épül, az index mindenhol ebből számolódik (`tabIndex(tabs, 'attendance')`), és a mobil
 * alsó sáv is ugyanebből a listából dolgozik — így a „Hírek" nem maradhat ki egy szerepkörnél.
 */
export type TabKey =
  | 'calendar' | 'profile' | 'stats' | 'availability' | 'attendance' | 'news'
  | 'reports' | 'settings' | 'sos' | 'coachPayments' | 'messages' | 'myPayments';

export interface TabDef {
  key: TabKey;
  /** Asztali fül felirata (emojival, mint eddig). */
  label: string;
  /** Mobil alsó sáv felirata (rövid, emoji nélkül). */
  mobileLabel: string;
  /** Szerepel-e a mobil alsó sávban. */
  mobile: boolean;
  /**
   * Telefonon a sáv végi „Továbbiak" menüben érhető el (csak `mobile: false` mellett értelmes).
   * [Web UX 9 / T4, 2026-10-13] MI VOLT A BAJ (2. körös UX-lista (`Documents/WEB_UX_JAVASLATOK_2KOR_2026-10-04.md`) 2.1): az admin 8 sáv-eleme
   * (8 × 72 px = 576 px) 360/390 px-en nem fért ki, a sáv vízszintesen görgetett, és a
   * „Beállítások" (a teendő-jelvénnyel, dosszié 5.33 nyitott (2)) görgetés nélkül NEM látszott;
   * az SOS Napló (`mobile: false`) és az admin Elérhetőség telefonon egyáltalán nem volt elérhető
   * a navigációból, csak `?ful=` linkkel. MÉRVE (Chromium, 0.7rem felirat, 4 px oldalsó belső margó):
   * az admin napi útja (Naptár, Üzenetek, Jelenlét, Beállítások, Kifizetések) + „Továbbiak" természetes
   * (zsugorítás/nyújtás nélküli) szélessége ≈ 335 px (48 + 55,3 + 48 + 62,8 + 62,2 + 58,4; a validátor M5
   * szerint javítva a korábbi „351"-ről) → 360 px-en görgetés nélkül kifér; egy hetedik elem (Adatlap, 48 px) már nem. Ezért az admin ritkán
   * használt fülei (saját Adatlap, Elérhetőség, Hírek, Kimutatások, SOS Napló) a „Továbbiak" alá kerültek.
   */
  mobileMore?: boolean;
  /** Mobil sorrend (kisebb elöl); alapból az asztali sorrend. */
  mobileOrder?: number;
}

export function buildTabs(isAdmin: boolean, isCoach: boolean): TabDef[] {
  const tabs: TabDef[] = [
    { key: 'calendar', label: '📅 Naptár', mobileLabel: 'Naptár', mobile: true },
    // [Web UX 9 / T4, 2026-10-13] Az admin saját Adatlapja ritka út → „Továbbiak" (lásd `mobileMore`).
    { key: 'profile', label: '📝 Adatlap', mobileLabel: 'Adatlap', mobile: !isAdmin, mobileMore: isAdmin },
  ];
  if (!isAdmin) {
    tabs.push({
      key: 'stats',
      label: isCoach ? '📊 Saját statisztikáim' : '📊 Statisztika',
      mobileLabel: isCoach ? 'Statisztika' : 'Egyenleg',
      mobile: true,
    });
  }
  if (isAdmin || isCoach) {
    // [klub-kérés] Az ADMIN alsó sávjában az Elérhetőség HELYÉRE az Üzenetek került (nyolc elem
    // már kitölti a sávot); az edzőnek az elérhetőség napi munkaeszköz, nála marad.
    // [Web UX 9 / T4, 2026-10-13] Az adminnál eddig telefonon SEHOGY nem volt elérhető — most a „Továbbiak" alatt.
    tabs.push({ key: 'availability', label: '⏰ Edző Elérhetőség', mobileLabel: 'Elérhetőség', mobile: !isAdmin, mobileMore: isAdmin });
    tabs.push({ key: 'attendance', label: '👥 Jelenlét', mobileLabel: 'Jelenlét', mobile: true });
  }
  // A Hírek MINDEN szerepkörnek jár — mobilon is (eddig az edző/játékos sávjából hiányzott).
  // [Web UX 9 / T4, 2026-10-13] Az adminnak a Hírek (szerkesztés) nem napi út → „Továbbiak"; edzőnél/játékosnál a sávban marad.
  tabs.push({ key: 'news', label: '📰 Hírek', mobileLabel: 'Hírek', mobile: !isAdmin, mobileMore: isAdmin });
  if (isAdmin) {
    // [Web UX 9 / T4, 2026-10-13] Kimutatás: havi/időszakos munka → „Továbbiak". A Beállítások (jóváhagyások,
    // teendő-jelvény) a SÁVBAN marad — a jelvénynek görgetés nélkül látszania kell (dosszié 5.33 nyitott (2)).
    tabs.push({ key: 'reports', label: '📊 Kimutatások', mobileLabel: 'Kimutatások', mobile: false, mobileMore: true });
    tabs.push({ key: 'settings', label: '⚙️ Beállítások', mobileLabel: 'Beállítások', mobile: true });
    // [Web UX 9 / T4, 2026-10-13] Eddig `mobile: false` volt: `?ful=sos-naplo` linkkel megnyílt, de nem volt belépési pont.
    tabs.push({ key: 'sos', label: '🔒 SOS Napló', mobileLabel: 'SOS Napló', mobile: false, mobileMore: true });
    tabs.push({ key: 'coachPayments', label: '💰 Edző Kifizetések', mobileLabel: 'Kifizetések', mobile: true });
    // Mobilon az Elérhetőség helyén (a Jelenlét előtt) — a klub kérése szerint.
    tabs.push({ key: 'messages', label: '📧 Üzenetek', mobileLabel: 'Üzenetek', mobile: true, mobileOrder: 2.5 });
  }
  if (isCoach) {
    tabs.push({ key: 'myPayments', label: '💰 Kifizetéseim', mobileLabel: 'Kifizetések', mobile: true });
  }
  return tabs;
}

/** A fül indexe a listában; -1, ha az adott szerepkörnek nincs ilyen füle. */
export function tabIndex(tabs: TabDef[], key: TabKey): number {
  return tabs.findIndex((t) => t.key === key);
}

/** A mobil alsó sáv elemei: csak a `mobile` fülek, a mobil sorrendben, az asztali indexszel mint értékkel. */
export function mobileNavItems(tabs: TabDef[]): Array<TabDef & { value: number }> {
  return tabs
    .map((t, value) => ({ ...t, value }))
    .filter((t) => t.mobile)
    .sort((a, b) => (a.mobileOrder ?? a.value) - (b.mobileOrder ?? b.value));
}

/**
 * A mobil „Továbbiak" menü elemei (a sávban NEM szereplő, `mobileMore` fülek), az asztali sorrendben,
 * az asztali indexszel mint értékkel — ugyanaz az érték, amit a sáv elemei is adnak, így a menüből
 * választás a Dashboard UGYANAZON útján megy (`handleMobileTabChange` → mentetlen-kapu → `?ful=`).
 * Üres lista → nincs „Továbbiak" elem (edző, játékos). [Web UX 9 / T4, 2026-10-13]
 */
export function mobileMoreItems(tabs: TabDef[]): Array<TabDef & { value: number }> {
  return tabs
    .map((t, value) => ({ ...t, value }))
    .filter((t) => !t.mobile && t.mobileMore);
}

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * A FÜL AZ URL-BEN — `?ful=<slug>&al=<slug>`  [Web UX 3. csomag (A), 2026-10-04]
 * ══════════════════════════════════════════════════════════════════════════════════════════
 *
 * MI VOLT A BAJ (jelentés 1.1): a Dashboard aktív füle sima állapot volt (`Dashboard.tsx`
 * `useState(0)`), az URL-ben semmi: F5 után a felület mindig a Naptárra ugrott, a böngésző Vissza
 * gombja kilépett az oldalról, és egy fület nem lehetett linkként továbbadni. A Beállítások
 * al-füle (`SettingsManagement.tsx` `useState(0)`) ugyanígy.
 *
 * MOST: a fül és az al-fül a lekérdezés-paraméterben él, MAGYAR sluggal (a `/helyszinek`,
 * `/belepes` útvonalak mintájára). Ez az EGYETLEN slug-tábla: a Dashboard, a Beállítások és a
 * tesztek is innen olvasnak. A `Record<…>` típus miatt egy új `TabKey` / al-fül slug nélkül le sem
 * fordul; az egyediséget a `dashboardTabs.urlSlugs.test.ts` őrzi.
 */

/** A lekérdezés-paraméterek nevei. */
export const TAB_PARAM = 'ful';
export const SUB_PARAM = 'al';

/** A böngészőfül címe a Dashboardon kívül (nyitóoldal, belépés) — egyezik a `public/index.html`-lel. */
export const APP_TITLE = 'BK Pingpong';

/**
 * Meddig várja a Dashboard a saját „Továbblépés" utáni `history.back()`-jének `popstate`-jét, amit a
 * router felé átenged (`Dashboard.tsx` `leaveDashboardViaHistory`). Ha ennyi idő alatt nem jön, a
 * jelző lejár, hogy egy KÉSŐBBI, kapuzandó Vissza-t ne engedjen át csendben. A valódi böngészőben a
 * `popstate` ezredmásodpercek alatt érkezik; a lejárat utáni késői `popstate` legrosszabb esetben
 * még egyszer rákérdez (adat nem vész el). [3. csomag (A), 2. javító kör, 2026-10-04]
 */
export const HISTORY_PASS_THROUGH_TIMEOUT_MS = 1500;

/**
 * A NEM a Dashboardot rajzoló útvonalak — az `App.tsx` `<Routes>`-ának MINDEN konkrét útvonala (a
 * `/*` kivételével, ami az `AuthWrapper` → bejelentkezve `Dashboard`).
 * [3. csomag (A), 2. validátor-kör K-1/F-A', 2026-10-04]
 *
 * MI VOLT A BAJ: a Dashboard a „saját útvonalát" EGYETLEN `pathname`-ként jegyezte meg (amin
 * felcsatolódott). Mivel a `/*` minden ismeretlen útvonalon is a Dashboardot rajzolja, egy két
 * Dashboard-útvonal közötti Vissza (pl. `/valami` → `/`) a router számára ugyanaz az elem maradt
 * (nincs lebontás), a Dashboard viszont „kilépésnek" vette: nem váltott fület (az URL és a képernyő
 * szétvált), mentetlen adatnál pedig hamis „Mentetlen változás" kérdést nyitott.
 *
 * MOST: Dashboard-útvonal = MINDEN, ami NEM ezek egyike (pontosan úgy, ahogy a router dönt). Az
 * illesztés a react-router saját `matchPath`-ja (kis-nagybetű FÜGGETLEN és a záró `/`-t is elfogadja,
 * mint a `<Route>` alapból — `/Helyszinek/` is a helyszínek oldala, nem Dashboard).
 * MIÉRT LISTA, és nem „a felcsatoláskor `/`-ra normalizált cím": az utóbbihoz a router `navigate`-je
 * (hook) kellene, a Dashboard viszont router-kontextus nélkül is renderelhető (a `Dashboard*.test.tsx`
 * készletek így futnak), és a normalizálás a felhasználó által beírt címet csendben átírná.
 * ⚠️ Ha az `App.tsx`-be ÚJ útvonal kerül, ide is fel kell venni — a `dashboardTabs.urlSlugs.test.ts`
 * forrás-őre az `App.tsx` és az `AuthWrapper.tsx` útvonal-definícióiból ellenőrzi, hogy a két lista egyezik.
 */
export const NON_DASHBOARD_ROUTE_PATHS: readonly string[] = [
  '/verify-email',
  '/forgot-password',
  '/reset-password',
  '/helyszinek',
  '/belepes',
  '/regisztracio',
  '/login',
];

/** A `pathname`-et a Dashboard rajzolja-e (az `App.tsx` `/*` útvonala) — lásd `NON_DASHBOARD_ROUTE_PATHS`. */
export function isDashboardPath(pathname: string): boolean {
  return !NON_DASHBOARD_ROUTE_PATHS.some((path) => matchPath({ path, caseSensitive: false, end: true }, pathname) !== null);
}

/** Minden fül-kulcs futásidőben is (a teszt és a slug-visszakeresés ebből dolgozik). */
export const ALL_TAB_KEYS: readonly TabKey[] = [
  'calendar', 'profile', 'stats', 'availability', 'attendance', 'news',
  'reports', 'settings', 'sos', 'coachPayments', 'messages', 'myPayments',
];

/**
 * Fül → slug. A `stats` slugja az edzőnél is `egyenleg` (a felirata ott „Statisztika", de a slug
 * egy fülhöz EGY legyen, szerepkörtől függetlenül — különben egy továbbküldött link szerepkörönként
 * mást jelentene).
 */
export const TAB_SLUGS: Record<TabKey, string> = {
  calendar: 'naptar',
  profile: 'adatlap',
  stats: 'egyenleg',
  availability: 'elerhetoseg',
  attendance: 'jelenlet',
  news: 'hirek',
  reports: 'kimutatasok',
  settings: 'beallitasok',
  sos: 'sos-naplo',
  coachPayments: 'kifizetesek',
  messages: 'uzenetek',
  myPayments: 'kifizeteseim',
};

/**
 * A Beállítások al-füleinek STABIL kulcsai. Az al-fület kulccsal (nem indexszel) kötjük a
 * `SettingsManagement` elemeihez: ma mind a 11 al-fül minden adminnak látszik, de ha egyszer
 * szerepkörfüggő vagy feltételes lesz egy elem, az index-alapú URL csendben MÁS al-fülre mutatna
 * (ugyanaz a hibaosztály, ami miatt a Dashboard fül-listája is kulcsos lett — lásd fent, A5).
 * A sorrend itt a `SettingsManagement` mai sorrendje (tájékoztató; a megjelenítés sorrendjét a
 * `SettingsManagement` adja).
 */
export type SettingsSubKey =
  | 'approvals' | 'reservations' | 'lessonRequests' | 'weeklyPlanner' | 'balances' | 'lessonDebts'
  | 'users' | 'venues' | 'eventTypes' | 'auditLog' | 'dataRetention';

export const ALL_SETTINGS_SUB_KEYS: readonly SettingsSubKey[] = [
  'approvals', 'reservations', 'lessonRequests', 'weeklyPlanner', 'balances', 'lessonDebts',
  'users', 'venues', 'eventTypes', 'auditLog', 'dataRetention',
];

export const SETTINGS_SUB_SLUGS: Record<SettingsSubKey, string> = {
  approvals: 'regisztraciok',
  reservations: 'asztalfoglalasok',
  lessonRequests: 'maganora-igenylesek',
  weeklyPlanner: 'heti-beosztas',
  balances: 'egyenlegek',
  lessonDebts: 'maganora-tartozasok',
  users: 'felhasznalok',
  venues: 'helyszinek',
  eventTypes: 'esemenytipusok',
  auditLog: 'audit-naplo',
  dataRetention: 'adattorles',
};

/** A Beállítások alapértelmezett al-füle (az első elem — a régi `useState(0)` viselkedése). */
export const DEFAULT_SETTINGS_SUB: SettingsSubKey = 'approvals';

export function tabKeyFromSlug(slug: string | null | undefined): TabKey | null {
  if (!slug) return null;
  return ALL_TAB_KEYS.find((k) => TAB_SLUGS[k] === slug) ?? null;
}

export function settingsSubKeyFromSlug(slug: string | null | undefined): SettingsSubKey | null {
  if (!slug) return null;
  return ALL_SETTINGS_SUB_KEYS.find((k) => SETTINGS_SUB_SLUGS[k] === slug) ?? null;
}

/** A Dashboard „képernyője": a fül és (csak a Beállításoknál értelmes) az al-fül. */
export interface DashboardScreen {
  tab: TabKey;
  sub: SettingsSubKey;
}

/**
 * Az URL lekérdezés-részéből a megjelenítendő képernyő, a SZEREPKÖR szerint ellenőrizve:
 *   · ismeretlen slug, vagy a szerepkörnek nem járó fül (pl. játékosnál `sos-naplo`) → Naptár;
 *   · az `al=` csak a Beállítások fülön számít (és az csak adminnak jár) — máshol figyelmen kívül;
 *   · ismeretlen `al=` → az alapértelmezett al-fül.
 */
export function screenFromSearch(search: string, tabs: TabDef[]): DashboardScreen {
  const params = new URLSearchParams(search);
  const key = tabKeyFromSlug(params.get(TAB_PARAM));
  const tab: TabKey = key && tabIndex(tabs, key) >= 0 ? key : 'calendar';
  const sub = tab === 'settings'
    ? settingsSubKeyFromSlug(params.get(SUB_PARAM)) ?? DEFAULT_SETTINGS_SUB
    : DEFAULT_SETTINGS_SUB;
  return { tab, sub };
}

/** Ugyanazt a képernyőt jelenti-e a kettő (az al-fül csak a Beállításoknál számít). */
export function sameScreen(a: DashboardScreen, b: DashboardScreen): boolean {
  return a.tab === b.tab && (a.tab !== 'settings' || a.sub === b.sub);
}

/**
 * A képernyő lekérdezés-része (`?…`), a MEGLÉVŐ, idegen paramétereket megtartva. `screen === null`
 * → a `ful`/`al` törlése (kijelentkezés). Üres eredménynél `''` (nem `'?'`).
 */
export function searchForScreen(currentSearch: string, screen: DashboardScreen | null): string {
  const params = new URLSearchParams(currentSearch);
  params.delete(TAB_PARAM);
  params.delete(SUB_PARAM);
  if (screen) {
    params.set(TAB_PARAM, TAB_SLUGS[screen.tab]);
    if (screen.tab === 'settings') params.set(SUB_PARAM, SETTINGS_SUB_SLUGS[screen.sub]);
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

/**
 * A kanonikus lekérdezés-rész a JAVÍTÁSHOZ (`replaceState`): ha az URL-ben NINCS `ful=`, akkor a
 * tiszta cím tiszta marad (csak egy árva `al=` törlődik) — a belépés utáni `/` nem kap
 * `?ful=naptar`-t (`flow-session-expired.spec.ts` 4. teszt a `/`-t várja).
 */
export function canonicalSearch(currentSearch: string, screen: DashboardScreen): string {
  const params = new URLSearchParams(currentSearch);
  if (!params.has(TAB_PARAM)) {
    if (!params.has(SUB_PARAM)) return currentSearch;
    return searchForScreen(currentSearch, null);
  }
  return searchForScreen(currentSearch, screen);
}

/** A böngészőfül címe a Dashboardon: `<fül rövid felirata> – BK Pingpong` (emodzsi nélkül). */
export function dashboardTitle(tabs: TabDef[], key: TabKey): string {
  const tab = tabs.find((t) => t.key === key);
  return tab ? `${tab.mobileLabel} – ${APP_TITLE}` : APP_TITLE;
}
